import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase/config';
import { supabase, generateTimeKeys, generateGeohash } from '../config/supabase';
import type { AnalyticsEvent } from '../config/supabase';

/**
 * Service de batch unifié pour les analytics GA4 et Supabase
 * Optimise les performances et réduit les coûts d'écriture
 */
export class AnalyticsBatchService {
  private static instance: AnalyticsBatchService | null = null;
  
  // Configuration
  private readonly BATCH_INTERVAL_MS = 30000; // 30 secondes - facilement modifiable
  private readonly MAX_BATCH_SIZE = 500; // Limite Firestore
  private readonly STORAGE_KEY = 'pendingAnalytics';
  
  // Files d'attente
  private pendingGA4Events: Array<{ eventName: string; params: any }> = [];
  private pendingSupabaseEvents: Array<AnalyticsEvent> = [];
  
  // Timer
  private batchTimer: NodeJS.Timeout | null = null;
  
  // Événements à envoyer vers Supabase (dashboard organisateurs)
  // NOTE: debut_session retiré - envoyé uniquement à GA4.
  // Le Session Resilience Pattern fait que debut_session fire avant la géoloc,
  // donc il était systématiquement filtré (geohash=unknown). connexion_zone suffit.
  private readonly SUPABASE_EVENTS = new Set([
    'connexion_zone',       // Connexions zone (géolocalisation, recherche, navigation)
    'apparition_evenement', // Apparitions événement
    'clic_evenement',       // Clics événement
    'like_evenement',       // Likes événement
    'organizer_login'       // Connexion organisateur (dashboard admin - 1x par session)
  ]);

  // Détection de bots/headless (audit GA4 2026-06-12 : ~10% du trafic = datacenters US).
  // Évalué une seule fois : navigator.webdriver couvre Puppeteer/Playwright/Selenium,
  // le regex couvre les crawlers qui exécutent le JS (Googlebot, prerender, monitoring...).
  private static readonly BOT_UA_REGEX =
    /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|pingdom|prerender|phantomjs|puppeteer|playwright|selenium|screenshot|gtmetrix|uptime|monitor|dataprovider|facebookexternalhit|whatsapp|telegrambot/i;

  private readonly isBotSession: boolean = (() => {
    try {
      if (typeof navigator === 'undefined') return true;
      if ((navigator as any).webdriver === true) return true;
      return AnalyticsBatchService.BOT_UA_REGEX.test(navigator.userAgent || '');
    } catch {
      return false;
    }
  })();

  private constructor() {
    this.checkSupabaseConfiguration();
    this.setupBeforeUnloadHandler();
    this.restorePendingEvents();
    this.startBatchTimer();
    if (this.isBotSession) {
      console.log('🤖 [Analytics] Bot/headless détecté — tracking désactivé pour cette session');
      this.reportBotSession();
    }
  }

  /**
   * Émet UN événement GA4 'bot_detected' par session bot (jamais vers Supabase),
   * pour que la part de trafic bot reste mesurable dans le dashboard admin
   * (remonte via eventCounts de l'edge function ga4-analytics) alors même que
   * tous les autres événements de la session sont bloqués.
   */
  private reportBotSession(): void {
    try {
      if (sessionStorage.getItem('w2d_bot_reported')) return;
      sessionStorage.setItem('w2d_bot_reported', '1');
      const detection = (navigator as any).webdriver === true ? 'webdriver' : 'user_agent';
      logEvent(analytics, 'bot_detected', { detection });
    } catch {
      // sessionStorage ou GA4 indisponible : tant pis, on ne mesure pas cette session
    }
  }

  /**
   * Vérifier et logger la configuration Supabase
   * Aide au diagnostic des problèmes en localhost
   */
  private checkSupabaseConfiguration(): void {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    const isConfigured = !!(supabaseUrl && supabaseKey);

    if (!isConfigured) {
      console.warn('⚠️ [Analytics] Supabase not configured - analytics will not be sent to Supabase');
      console.warn('[Analytics] SUPABASE_URL:', supabaseUrl ? '✅ SET' : '❌ MISSING');
      console.warn('[Analytics] SUPABASE_KEY:', supabaseKey ? '✅ SET' : '❌ MISSING');
      console.warn('[Analytics] Check your .env.local file and restart the server');
    } else {
      console.log('✅ [Analytics] Supabase configured');
      console.log(`[Analytics] URL: ${supabaseUrl?.substring(0, 30)}...`);
    }
  }

  /**
   * Vérifier si Supabase est configuré (pour skip des envois si non configuré)
   */
  private isSupabaseConfigured(): boolean {
    return !!(
      process.env.REACT_APP_SUPABASE_URL &&
      process.env.REACT_APP_SUPABASE_ANON_KEY
    );
  }

  /**
   * Singleton pattern
   */
  static getInstance(): AnalyticsBatchService {
    if (!this.instance) {
      this.instance = new AnalyticsBatchService();
    }
    return this.instance;
  }

  /**
   * Ajouter un événement au batch
   */
  addEvent(eventName: string, eventParams: any): void {
    // Bots/headless : on ne pollue ni GA4 ni Supabase (les chiffres du dashboard
    // admin et les stats organisateurs doivent refléter de vrais touristes)
    if (this.isBotSession) return;

    // Tous les événements vont vers GA4
    this.pendingGA4Events.push({ eventName, params: eventParams });

    // Seuls certains événements vont vers Supabase (dashboard organisateurs)
    if (this.SUPABASE_EVENTS.has(eventName)) {
      console.log(`🔍 [SUPABASE DEBUG] Event "${eventName}" détecté pour Supabase:`, eventParams);
      const supabaseEvent = this.convertToSupabaseEvent(eventName, eventParams);
      if (supabaseEvent) {
        console.log(`✅ [SUPABASE DEBUG] Event converti:`, supabaseEvent);
        this.pendingSupabaseEvents.push(supabaseEvent);
      } else {
        console.log(`❌ [SUPABASE DEBUG] Event "${eventName}" ignoré (conversion échouée)`);
      }
    } else {
      console.log(`🚫 [SUPABASE DEBUG] Event "${eventName}" ignoré (pas dans SUPABASE_EVENTS)`);
    }

    // Sauvegarder dans localStorage
    this.saveToLocalStorage();

    // Envoyer immédiatement si batch plein
    if (this.pendingGA4Events.length >= this.MAX_BATCH_SIZE) {
      this.flushBatch();
    }
  }

  /**
   * Convertir un événement analytics vers le format Supabase
   * Utilise geohash pour l'indexation spatiale (precision 5 = ~4.9km)
   *
   * IMPORTANT: Seul debut_session nécessite obligatoirement une géolocalisation
   * Les autres events (impressions, clics, likes) fonctionnent sans géoloc
   */
  private convertToSupabaseEvent(eventName: string, params: any): AnalyticsEvent | null {
    try {
      const timeKeys = generateTimeKeys();

      // Générer geohash depuis localisation ou paramètres
      let userGeohash = 'unknown';
      if (params.localisation_utilisateur) {
        userGeohash = generateGeohash(
          params.localisation_utilisateur.lat,
          params.localisation_utilisateur.lng
        );
      } else if (params.zone_geographique_id) {
        userGeohash = params.zone_geographique_id;
      }

      // Pour connexion_zone, la géolocalisation est OBLIGATOIRE
      const isZoneEvent = eventName === 'connexion_zone';
      if (isZoneEvent && userGeohash === 'unknown') {
        console.log(`⚠️ [SUPABASE] ${eventName} ignoré: pas de géolocalisation`);
        return null;
      }

      const baseEvent: AnalyticsEvent = {
        event_name: eventName,
        geohash: userGeohash,
        day_key: timeKeys.day_key,
        week_key: timeKeys.week_key,
        metadata: {}
      };

      // Ajouter données spécifiques selon le type d'événement
      if (isZoneEvent) {
        // Connexions zone - pas besoin d'organisateur
        baseEvent.metadata = {
          user_type: params.type_utilisateur || 'touriste',
          source: params.source || params.source_utilisateur || 'unknown'
        };
      } else {
        // Événements avec organisateur/événement (impressions, clics, likes)
        if (params.id_organisateur) baseEvent.organizer_id = params.id_organisateur;
        if (params.id_evenement) baseEvent.event_id = params.id_evenement;

        baseEvent.metadata = {
          vue_source: params.vue_source || 'unknown',
          methode: params.methode || params.methode_decouverte || 'unknown',
          ...(params.duration_ms != null && { duration_ms: params.duration_ms })
        };
      }

      return baseEvent;
    } catch (error) {
      console.error('❌ Erreur conversion Supabase:', error);
      return null;
    }
  }

  /**
   * Envoyer tous les événements en attente
   */
  async flushBatch(): Promise<void> {
    if (this.pendingGA4Events.length === 0 && this.pendingSupabaseEvents.length === 0) {
      return;
    }

    console.log(`🚀 Sending batch: ${this.pendingGA4Events.length} GA4 events, ${this.pendingSupabaseEvents.length} Supabase events`);

    try {
      // Envoyer vers GA4 (en batch si possible)
      await this.sendGA4Batch();
      
      // Envoyer vers Supabase (en batch)
      await this.sendSupabaseBatch();

      // Vider les files d'attente
      this.pendingGA4Events = [];
      this.pendingSupabaseEvents = [];
      
      // Nettoyer localStorage
      localStorage.removeItem(this.STORAGE_KEY);

      console.log('✅ Batch sent successfully');

    } catch (error) {
      console.error('❌ Batch sending error:', error);
      // Les événements restent en file d'attente pour retry
    }
  }

  /**
   * Envoyer les événements GA4
   */
  private async sendGA4Batch(): Promise<void> {
    for (const event of this.pendingGA4Events) {
      try {
        const cleanParams = this.cleanFirebaseParams(event.params);
        logEvent(analytics, event.eventName, cleanParams);
      } catch (error) {
        console.error(`❌ GA4 event error (${event.eventName}):`, error);
      }
    }
  }

  /**
   * Envoyer les événements Supabase en batch
   */
  private async sendSupabaseBatch(): Promise<void> {
    if (this.pendingSupabaseEvents.length === 0) return;

    // Skip si Supabase non configuré (utile pour localhost)
    if (!this.isSupabaseConfigured()) {
      console.warn(`⏭️ [Analytics] Skipping Supabase batch (${this.pendingSupabaseEvents.length} events) - not configured`);
      return;
    }

    try {
      // Envoyer tous les événements en une seule requête batch
      // TODO: Supprimer 'as any' après génération des types via: npm run supabase:types
      const { error } = await supabase
        .from('analytics_events')
        .insert(this.pendingSupabaseEvents as any);

      if (error) {
        throw error;
      }

      console.log(`✅ Supabase batch: ${this.pendingSupabaseEvents.length} events sent`);

      // 📊 Tracking monitoring équivalent
      console.log(`📊 AnalyticsBatch → analytics_events: ${this.pendingSupabaseEvents.length} events (batch analytics)`);

    } catch (error) {
      console.error('❌ Supabase batch error:', error);
      throw error; // Re-throw pour déclencher le retry
    }
  }

  /**
   * Nettoyer les paramètres pour Firebase Analytics
   */
  private cleanFirebaseParams(params: any): any {
    const cleanParams: any = {};
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      
      if (value !== null && value !== undefined) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          cleanParams[key] = value;
        } else if (Array.isArray(value)) {
          cleanParams[key] = value.join(',');
        } else if (typeof value === 'object') {
          cleanParams[key] = JSON.stringify(value);
        }
      }
    });
    
    return cleanParams;
  }

  /**
   * Démarrer le timer de batch
   */
  private startBatchTimer(): void {
    this.batchTimer = setInterval(() => {
      this.flushBatch();
    }, this.BATCH_INTERVAL_MS);
  }

  /**
   * Arrêter le timer de batch
   */
  private stopBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  /**
   * Sauvegarder dans localStorage
   */
  private saveToLocalStorage(): void {
    try {
      const data = {
        ga4Events: this.pendingGA4Events,
        supabaseEvents: this.pendingSupabaseEvents,
        timestamp: Date.now()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('❌ localStorage save error:', error);
    }
  }

  /**
   * Restaurer les événements depuis localStorage
   */
  private restorePendingEvents(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        
        // Restaurer seulement si récent (< 5 minutes)
        if (Date.now() - data.timestamp < 300000) {
          this.pendingGA4Events = data.ga4Events || [];
          this.pendingSupabaseEvents = data.supabaseEvents || [];
          console.log(`🔄 Restored ${this.pendingGA4Events.length} GA4 + ${this.pendingSupabaseEvents.length} Supabase events from localStorage`);
        } else {
          localStorage.removeItem(this.STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('❌ localStorage restore error:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Handler pour envoyer les events avant fermeture de page
   * Utilise navigator.sendBeacon pour Supabase (fiable sur beforeunload)
   * et logEvent synchrone pour GA4
   */
  private setupBeforeUnloadHandler(): void {
    const flushOnExit = () => {
      // 1. Envoyer GA4 de façon synchrone (Firebase SDK gère en interne)
      this.sendGA4Batch();

      // 2. Envoyer Supabase via sendBeacon (fiable même pendant beforeunload)
      this.sendSupabaseViaBeacon();

      // 3. Vider les queues (on a tout envoyé)
      this.pendingGA4Events = [];
      this.pendingSupabaseEvents = [];
      localStorage.removeItem(this.STORAGE_KEY);
    };

    window.addEventListener('beforeunload', flushOnExit);

    // Également pour visibilitychange (mobile - page mise en arrière-plan)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        flushOnExit();
      }
    });
  }

  /**
   * Envoyer les events Supabase via navigator.sendBeacon
   * sendBeacon est garanti d'être envoyé même pendant beforeunload
   */
  private sendSupabaseViaBeacon(): void {
    if (this.pendingSupabaseEvents.length === 0) return;
    if (!this.isSupabaseConfigured()) return;

    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) return;

      const url = `${supabaseUrl}/rest/v1/analytics_events`;
      const blob = new Blob(
        [JSON.stringify(this.pendingSupabaseEvents)],
        { type: 'application/json' }
      );

      // sendBeacon ne supporte pas les headers custom, on utilise un Blob avec les headers encodés
      // Pour Supabase REST API, on doit utiliser fetch avec keepalive à la place
      const sent = navigator.sendBeacon(url + `?apikey=${supabaseKey}`, blob);

      if (!sent) {
        // Fallback : fetch avec keepalive (fonctionne aussi pendant beforeunload)
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify(this.pendingSupabaseEvents),
          keepalive: true
        }).catch(() => {
          // Dernier recours : sauvegarder pour restauration
          this.saveToLocalStorage();
        });
      }

      console.log(`📡 [Analytics] sendBeacon: ${this.pendingSupabaseEvents.length} Supabase events`);
    } catch (error) {
      // En cas d'erreur, sauvegarder pour la prochaine session
      this.saveToLocalStorage();
    }
  }

  /**
   * Méthodes publiques pour configuration
   */
  
  /**
   * Changer l'intervalle de batch (en millisecondes)
   */
  setBatchInterval(intervalMs: number): void {
    this.stopBatchTimer();
    (this as any).BATCH_INTERVAL_MS = intervalMs;
    this.startBatchTimer();
    console.log(`🔧 Batch interval changed to ${intervalMs}ms`);
  }

  /**
   * Forcer l'envoi immédiat du batch
   */
  forceBatch(): void {
    this.flushBatch();
  }

  /**
   * Obtenir les statistiques du batch
   */
  getStats(): { ga4Pending: number; supabasePending: number; interval: number } {
    return {
      ga4Pending: this.pendingGA4Events.length,
      supabasePending: this.pendingSupabaseEvents.length,
      interval: this.BATCH_INTERVAL_MS
    };
  }

  /**
   * Nettoyer le service (pour tests)
   */
  cleanup(): void {
    this.stopBatchTimer();
    this.pendingGA4Events = [];
    this.pendingSupabaseEvents = [];
    localStorage.removeItem(this.STORAGE_KEY);
  }
} 